import React, { useState, useEffect } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import Input from './ui/Input';
import { HiCheck, HiMagnifyingGlass, HiXMark } from 'react-icons/hi2';

function ComplexFilterModal({
    isOpen,
    onClose,
    onApplyFilters,
    groups = [],
    teachers = [],
    subjects = [],
    rooms = [],
    initialSelectedGroups = [],
    initialSelectedTeachers = [],
    initialSelectedSubjects = [],
    initialSelectedRooms = []
}) {
    // State for selected items in each category
    const [selectedGroups, setSelectedGroups] = useState(initialSelectedGroups);
    const [selectedTeachers, setSelectedTeachers] = useState(initialSelectedTeachers);
    const [selectedSubjects, setSelectedSubjects] = useState(initialSelectedSubjects);
    const [selectedRooms, setSelectedRooms] = useState(initialSelectedRooms);

    // State for search filters
    const [groupSearch, setGroupSearch] = useState('');
    const [teacherSearch, setTeacherSearch] = useState('');
    const [subjectSearch, setSubjectSearch] = useState('');
    const [roomSearch, setRoomSearch] = useState('');

    // Reset state when modal opens with initial values
    useEffect(() => {
        if (isOpen) {
            setSelectedGroups(initialSelectedGroups);
            setSelectedTeachers(initialSelectedTeachers);
            setSelectedSubjects(initialSelectedSubjects);
            setSelectedRooms(initialSelectedRooms);
            // Reset search filters when modal opens
            setGroupSearch('');
            setTeacherSearch('');
            setSubjectSearch('');
            setRoomSearch('');
        }
    }, [isOpen, initialSelectedGroups, initialSelectedTeachers, initialSelectedSubjects, initialSelectedRooms]);

    // Helper function to toggle selection
    const toggleSelection = (item, category, setter) => {
        setter(prev => {
            if (prev.includes(item)) {
                return prev.filter(i => i !== item);
            } else {
                return [...prev, item];
            }
        });
    };

    // Helper function to handle "All *" selection
    const handleAllSelection = (category, setter) => {
        setter([]); // Empty array means "All" is selected
    };

    // Helper function to check if "All" is selected
    const isAllSelected = (selectedItems) => {
        return selectedItems.length === 0;
    };

    // Helper function to check if specific item is selected
    const isItemSelected = (item, selectedItems) => {
        return selectedItems.includes(item);
    };

    // Handle apply filters
    const handleApply = () => {
        onApplyFilters({
            groups: selectedGroups,
            teachers: selectedTeachers,
            subjects: selectedSubjects,
            rooms: selectedRooms
        });
        onClose();
    };

    // Handle clear all filters
    const handleClearAll = () => {
        setSelectedGroups([]);
        setSelectedTeachers([]);
        setSelectedSubjects([]);
        setSelectedRooms([]);
        // Also clear search filters
        setGroupSearch('');
        setTeacherSearch('');
        setSubjectSearch('');
        setRoomSearch('');
    };

    // Render list component
    const renderList = (title, items, selectedItems, setter, itemKey = 'name', searchValue, setSearchValue) => {
        const allItem = `All ${title}`;
        const isAllSelectedForCategory = isAllSelected(selectedItems);

        // Filter items based on search
        const filteredItems = items.filter(item => {
            const itemName = typeof item === 'string' ? item : (item[itemKey] || item.name || item.group_code || item.room_code || item.id || '');
            return itemName.toLowerCase().includes(searchValue.toLowerCase());
        });

        return (
            <div className="space-y-3">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                    {searchValue && (
                        <span className="text-xs text-gray-500">
                            {filteredItems.length} of {items.length}
                        </span>
                    )}
                </div>

                {/* Search Input */}
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <HiMagnifyingGlass className="h-4 w-4 text-gray-400" />
                    </div>
                    <Input
                        type="text"
                        placeholder={`Search ${title.toLowerCase()}...`}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="pl-10 pr-8 text-sm"
                    />
                    {searchValue && (
                        <button
                            onClick={() => setSearchValue('')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            aria-label="Clear search"
                        >
                            <HiXMark className="h-4 w-4" />
                        </button>
                    )}
                </div>

                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {/* All option */}
                    <div
                        className={`p-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${isAllSelectedForCategory ? 'bg-blue-50 border-blue-200' : ''
                            }`}
                        onClick={() => handleAllSelection(title, setter)}
                    >
                        <div className="flex items-center justify-between">
                            <span className={`font-medium ${isAllSelectedForCategory ? 'text-blue-700' : 'text-gray-700'}`}>
                                {allItem}
                            </span>
                            {isAllSelectedForCategory && (
                                <HiCheck className="h-5 w-5 text-blue-600" />
                            )}
                        </div>
                    </div>

                    {/* Individual items */}
                    {filteredItems.map((item, index) => {
                        const itemName = typeof item === 'string' ? item : (item[itemKey] || item.name || item.group_code || item.room_code || item.id || `Item ${index}`);
                        const itemValue = typeof item === 'string' ? item : (item[itemKey] || item.name || item.group_code || item.room_code || item.id);
                        const isSelected = isItemSelected(itemValue, selectedItems);

                        return (
                            <div
                                key={itemValue || index}
                                className={`p-3 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50 border-blue-200' : ''
                                    }`}
                                onClick={() => toggleSelection(itemValue, title, setter)}
                            >
                                <div className="flex items-center justify-between">
                                    <span className={`${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                                        {itemName}
                                    </span>
                                    {isSelected && (
                                        <HiCheck className="h-5 w-5 text-blue-600" />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Advanced Filters"
        >
            <div className="space-y-6">
                {/* Filter Lists */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {renderList('Groups', groups, selectedGroups, setSelectedGroups, 'name', groupSearch, setGroupSearch)}
                    {renderList('Teachers', teachers, selectedTeachers, setSelectedTeachers, 'name', teacherSearch, setTeacherSearch)}
                    {renderList('Subjects', subjects, selectedSubjects, setSelectedSubjects, 'name', subjectSearch, setSubjectSearch)}
                    {renderList('Rooms', rooms, selectedRooms, setSelectedRooms, 'name', roomSearch, setRoomSearch)}
                </div>

                {/* Filter Summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current Selection:</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                        <div>
                            <span className="font-medium">Groups:</span> {
                                isAllSelected(selectedGroups)
                                    ? 'All Groups'
                                    : selectedGroups.length === 0
                                        ? 'None selected'
                                        : selectedGroups.join(', ')
                            }
                        </div>
                        <div>
                            <span className="font-medium">Teachers:</span> {
                                isAllSelected(selectedTeachers)
                                    ? 'All Teachers'
                                    : selectedTeachers.length === 0
                                        ? 'None selected'
                                        : selectedTeachers.join(', ')
                            }
                        </div>
                        <div>
                            <span className="font-medium">Subjects:</span> {
                                isAllSelected(selectedSubjects)
                                    ? 'All Subjects'
                                    : selectedSubjects.length === 0
                                        ? 'None selected'
                                        : selectedSubjects.join(', ')
                            }
                        </div>
                        <div>
                            <span className="font-medium">Rooms:</span> {
                                isAllSelected(selectedRooms)
                                    ? 'All Rooms'
                                    : selectedRooms.length === 0
                                        ? 'None selected'
                                        : selectedRooms.join(', ')
                            }
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between">
                    <Button
                        variant="secondary"
                        onClick={handleClearAll}
                        className="text-gray-600"
                    >
                        Clear All
                    </Button>
                    <div className="flex gap-3">
                        <Button
                            variant="secondary"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleApply}
                        >
                            Apply Filters
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default ComplexFilterModal;
